import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useCallback, useEffect, useId, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { customToast } from "../../../../components/notify";
import { PickerDate } from "../../../analyze/context";
import { IAdFormData, IAdMedia, IApiAdMedia, IApiAdvertisement } from "../../types";
import { adValidationSchema } from "../../validations/validations";
import { INITIAL_AD_SETTINGS } from "../configs";
import useBatchUploadFiles from "./useBatchUploadFiles";
import useKioskSettings from "./useKioskSettings";
import { useTabGuard } from "./useTabGuard";

interface UseAdvertiseSettingsProps {
    characterId: string;
}

type AdFormWithFileFields = IAdFormData &
    Record<string, unknown> & {
        [key: `mediaFile_${number}`]: File | null;
    };

export const useAdvertiseSettings = ({ characterId }: UseAdvertiseSettingsProps) => {
    const { t } = useTranslation();
    const rawId = useId();
    const keyPrefix = rawId.replace(/^:/, ""); // 去除開頭的冒號

    const { kioskSetting, isLoadingKioskSetting, isUpdatingKioskSetting, updateAdvertisement } = useKioskSettings({
        characterId,
    });

    const { uploadAllFiles, isUploading } = useBatchUploadFiles();

    const generateMediaKey = useCallback(() => {
        return `${keyPrefix}${Date.now()}-${Math.random().toString(36).slice(2, 4)}`;
    }, [keyPrefix]);

    const convertFormDataToApiPayload = useCallback((formData: IAdFormData): IApiAdvertisement => {
        const apiMedia: IApiAdMedia[] = formData.media.map((media) => ({
            key: media.key,
            name: media.name,
            type: media.type,
            duration: media.duration,
            url: media.url,
            updated_at: media.updated_at,
            is_permanent: media.is_permanent,
            start_at: media.start_at ? dayjs(media.start_at).toISOString() : null,
            end_at: media.end_at ? dayjs(media.end_at).toISOString() : null,
        }));

        return {
            enabled: formData.enabled,
            shuffle_mode: formData.shuffle_mode,
            media: apiMedia,
        };
    }, []);

    const convertApiDataToFormData = useCallback((apiData: IApiAdvertisement): IAdFormData => {
        const formMedia: IAdMedia[] = apiData.media.map((media, index) => ({
            key: media.key,
            name: media.name || `廣告 ${index + 1}`,
            type: media.type,
            duration: media.duration,
            url: media.url,
            updated_at: media.updated_at,
            is_permanent: media.is_permanent,
            start_at: media.start_at ? dayjs(media.start_at) : null,
            end_at: media.end_at ? dayjs(media.end_at) : null,
        }));

        return {
            enabled: apiData.enabled,
            shuffle_mode: apiData.shuffle_mode,
            media: formMedia,
        };
    }, []);

    const methods = useForm<AdFormWithFileFields>({
        resolver: yupResolver(adValidationSchema),
        mode: "onChange",
        reValidateMode: "onChange",
        defaultValues: {
            enabled: INITIAL_AD_SETTINGS.enabled,
            shuffle_mode: INITIAL_AD_SETTINGS.shuffle_mode,
            media: INITIAL_AD_SETTINGS.media,
        },
    });
    const { control, handleSubmit, watch, setValue, formState } = methods;

    const {
        fields: mediaFields,
        append: appendMedia,
        remove: removeMedia,
    } = useFieldArray({
        control,
        name: "media" as never,
    });

    const watchedMedia = watch("media");
    const watchedEnabled = watch("enabled");
    const watchedFormValues = watch();

    // ======================= Toggle Handler =======================
    const isEnabled = watchedEnabled;
    const handleToggle = useCallback(
        (enabled: boolean) => {
            setValue("enabled", enabled, { shouldDirty: true });
        },
        [setValue]
    );

    const hasAllMediaFilesOrUrls = useMemo(() => {
        if (watchedMedia.length === 0) return true;

        return watchedMedia.every((media, index) => {
            const hasUrl = media.url && media.url.trim() !== "";
            const mediaFileFieldName = `mediaFile_${index}` as `mediaFile_${number}`;
            const hasFile = Boolean(watchedFormValues[mediaFileFieldName]);
            return hasUrl || hasFile;
        });
    }, [watchedMedia, watchedFormValues]);

    const handleAddMedia = () => {
        if (mediaFields.length >= 10) {
            customToast.error(t("APPWIDGET.AD_MAX_COUNTS_10") || "最多只能設定10組廣告");
            return;
        }

        const newMedia: IAdMedia = {
            key: generateMediaKey(),
            name: `廣告 ${mediaFields.length + 1}`,
            type: "image",
            duration: 10,
            url: "",
            updated_at: new Date().toISOString(),
            is_permanent: true,
            start_at: null,
            end_at: null,
        };

        appendMedia(newMedia);
    };

    const handleAddDefaultMedia = () => {
        // 檢查是否有足夠空間添加兩個廣告
        if (mediaFields.length >= 9) {
            customToast.error(t("APPWIDGET.AD_MAX_COUNTS_10") || "最多只能設定10組廣告");
            return;
        }
        // 預設廣告-A
        const defaultMediaA: IAdMedia = {
            key: generateMediaKey(),
            name: "預設廣告-A",
            type: "image",
            duration: 10,
            url: "https://cdn-tbd-warehouse.thebarkingdog.tw/frontend/default_ad_1.png",
            updated_at: new Date().toISOString(),
            is_permanent: true,
            start_at: null,
            end_at: null,
        };

        // 預設廣告-B
        const defaultMediaB: IAdMedia = {
            key: generateMediaKey(),
            name: "預設廣告-B",
            type: "image",
            duration: 10,
            url: "https://cdn-tbd-warehouse.thebarkingdog.tw/frontend/default_ad_2.png",
            updated_at: new Date().toISOString(),
            is_permanent: true,
            start_at: null,
            end_at: null,
        };

        // 一次添加兩個廣告
        appendMedia(defaultMediaA);
        appendMedia(defaultMediaB);

        customToast.success(t("APPWIDGET.AD_DEFAULT_CREATED") || "已建立2個預設廣告");
    };

    const handleRemoveMedia = (index: number) => {
        removeMedia(index);
    };

    const onSubmit = useCallback(
        async (data: AdFormWithFileFields) => {
            try {
                const allFormData = methods.getValues();
                const missingMedia: number[] = [];

                data.media.forEach((media, index) => {
                    const hasUrl = media.url && media.url.trim() !== "";
                    const mediaFileFieldName = `mediaFile_${index}` as `mediaFile_${number}`;
                    const hasFile = Boolean(allFormData[mediaFileFieldName]);

                    if (!hasUrl && !hasFile) {
                        missingMedia.push(index + 1);
                    }
                });

                if (missingMedia.length > 0) {
                    customToast.error(
                        `${t("APPWIDGET.AD_MISSING_MEDIA") || "請為以下廣告上傳檔案"}: ${missingMedia.join(", ")}`
                    );
                    return;
                }

                const dataWithUrls = await uploadAllFiles<AdFormWithFileFields>(allFormData);
                const sanitizedData: IAdFormData = {
                    enabled: dataWithUrls.enabled as boolean,
                    shuffle_mode: dataWithUrls.shuffle_mode as boolean,
                    media: dataWithUrls.media as IAdMedia[],
                };

                const payload = convertFormDataToApiPayload(sanitizedData);
                await updateAdvertisement(payload);
                customToast.success(t("COMMON.SETTINGS_SAVED") || "設定已儲存");
            } catch (error) {
                if (error instanceof Error && error.message.includes("Failed to upload")) {
                    throw error;
                }
                customToast.error(t("COMMON.SETTINGS_ERROR") || "儲存設定失敗");
                throw error; // 重新拋出錯誤以便 TabGuard 能夠捕獲
            }
        },
        [methods, uploadAllFiles, convertFormDataToApiPayload, updateAdvertisement, t]
    );

    const saveHandler = useCallback(async () => {
        await handleSubmit(onSubmit)();
    }, [handleSubmit, onSubmit]);

    useTabGuard("advertisement", methods.formState.isDirty, saveHandler);

    const getMediaDateRange = (media: IAdMedia) => {
        return {
            started_at: media.start_at ? dayjs(media.start_at) : null,
            ended_at: media.end_at ? dayjs(media.end_at) : null,
        };
    };

    const validateDateRange = (startDate: PickerDate, endDate: PickerDate) => {
        if (!startDate || !endDate) {
            return t("APPWIDGET.AD_SET_START_AND_END_TIME") || "請設定開始和結束時間";
        }

        const start = startDate.toDate();
        const end = endDate.toDate();

        if (end <= start) {
            return t("APPWIDGET.AD_END_TIME_LATER_THAN_START") || "結束時間必須晚於開始時間";
        }

        return null;
    };

    useEffect(() => {
        if (kioskSetting?.data?.native?.ad) {
            const formData = convertApiDataToFormData(kioskSetting.data.native.ad);
            methods.reset(formData as AdFormWithFileFields);
        }
    }, [kioskSetting?.data?.native?.ad, convertApiDataToFormData, methods]);

    // Auto-update updated_at timestamp when media fields change
    useEffect(() => {
        const subscription = watch((value, { name }) => {
            // Only update if a media field changed (but not updated_at itself)
            if (name && name.startsWith("media.") && !name.endsWith(".updated_at")) {
                const mediaIndexMatch = name.match(/^media\.(\d+)\./);
                if (mediaIndexMatch) {
                    const mediaIndex = parseInt(mediaIndexMatch[1], 10);
                    if (!isNaN(mediaIndex)) {
                        setValue(`media.${mediaIndex}.updated_at`, new Date().toISOString(), {
                            shouldDirty: true,
                            shouldValidate: true,
                        });
                    }
                }
            }
        });
        return () => subscription.unsubscribe();
    }, [watch, setValue]);

    return {
        // Form methods
        methods,
        handleSubmit,
        onSubmit,
        watch,
        setValue,
        formState,

        // Toggle state
        isEnabled,
        handleToggle,

        // State
        mediaFields,
        watchedMedia,
        watchedEnabled,
        hasAllMediaFilesOrUrls,

        // Loading states
        isLoadingKioskSetting,
        isUpdatingKioskSetting,
        isUploading,

        // Handlers
        handleAddMedia,
        handleAddDefaultMedia,
        handleRemoveMedia,
        getMediaDateRange,
        validateDateRange,
    };
};
